<template>
    <Menu as="div" class="relative inline-block text-left" v-slot="{ open }">
        <MenuButton
            v-if="permissionCheck"
            :disabled="disable"
            ref="menuButton"
            @click.capture="onButtonClick"
        >
            <slot name="dropdown-icon"></slot>
            <!-- 으로 드롭다운 아이콘 전달 <template v-slot:dropdown-icon></template> -->
        </MenuButton>
        <!-- teleport=true 일 때 overflow 컨테이너 클리핑을 피하기 위해 body로 빼내 fixed 로 띄움 -->
        <Teleport to="body" :disabled="!teleport">
            <transition @before-enter="onMenuOpen"
                        @before-leave="unbindReposition"
                        enter-active-class="transition ease-out duration-100"
                        enter-from-class="transform opacity-0 scale-95"
                        enter-to-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-from-class="transform opacity-100 scale-100"
                        leave-to-class="transform opacity-0 scale-95">
                <MenuItems
                    :static="teleport"
                    v-if="!teleport || open"
                    :style="teleport ? floatingStyle : null"
                    :class="[
                        teleport
                            ? 'fixed z-50'
                            : ['absolute z-10', dropRight ? 'right-0' : 'left-0', dropUp ? 'bottom-4' : ''],
                        dropdownWidth]"
                    class="mt-1 p-2 max-h-[400px] origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
                >
                    <div class="py-1">
                        <template v-for="(item, idx) in menuItems"  :key="idx">
                            <MenuItem v-slot="{ active }" @click="item.action" v-if="item.visible">
                                <a href="#" class="flex gap-2.5 px-3 py-2 font-body-sm-regular rounded-2 text-content-basic-1 items-center"
                                    :class="[active ? ' bg-background-basic-3 outline-none' : 'bg-background-basic-1 ']">
                                    <component v-if="item.icon" :is="item.icon" v-bind="item.iconProps"/>
                                    <span>{{ item.label }}</span>
                                </a>
                            </MenuItem>
                        </template>
                    </div>
                </MenuItems>
            </transition>
        </Teleport>
    </Menu>
</template>

<script>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import deleteIcon from './icon/delete.vue';
import infoIcon from './icon/info.vue';
import mailIcon from './icon/mail.vue';
import closeIcon from './icon/close.vue';

export default {
    components : {
        Menu, MenuButton, MenuItem, MenuItems,
        infoIcon, deleteIcon,mailIcon,closeIcon
    },
    props : {
        permissionCheck: { // 관련 권한이 없는 경우 보이지 않음 
            type: Boolean,
            default : true
        },
        dropdownWidth : { type : String, default : 'w-52' }, 
        disable: { // 관련 권한이 없는 경우 보이지 않음 
            type: Boolean,
            default : false
        },
        menuItems: {
            type: Array, // 메뉴 항목 객체들의 배열 각 항목은 다음 속성을 포함
            /*
                {
                label: String      // 메뉴에 표시될 텍스트
                action: Function   // 클릭 시 실행할 함수
                visible: Boolean   // 메뉴 표시 여부 (권한 등에 따라 제어)
                icon: Component    // 아이콘 컴포넌트의 이름(InfoIcon, TrashIcon, ect)
                iconProps: Object  // 아이콘 컴포넌트에 전달할 props (width, height, color, ect)
                }
            */
        },
        dropUp : { type : Boolean, default : false },
        dropRight : { type : Boolean, default : false },
        teleport : { // true 시 메뉴를 body로 teleport + fixed 배치 (overflow 컨테이너 클리핑 회피)
            type : Boolean,
            default : false
        },
        bypass : { // true 시 버튼 클릭 시 dropdown을 열지 않고 bypassAction을 바로 실행
            type : Boolean,
            default : false
        },
        bypassAction : { // bypass=true 일 때 클릭 시 실행할 함수
            type : Function,
            default : null
        },
    },
    data() {
        return {
            floatingStyle: {},
        };
    },
    methods: {
        onButtonClick(e) {
            // bypass=true 이면 HeadlessUI MenuButton 의 기본 토글 동작을 막고 bypassAction 실행
            if (!this.bypass) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            if (typeof this.bypassAction === 'function') {
                this.bypassAction();
            }
        },
        updatePosition() {
            if (!this.teleport) return;
            const el = this.$refs.menuButton?.$el;
            if (!el) return;
            const r = el.getBoundingClientRect();
            // 트리거가 뷰포트 밖으로 나가면 닫기 (열린 상태에서 버튼 클릭 → HeadlessUI closeMenu)
            if (r.bottom <= 0 || r.top >= window.innerHeight
                || r.right <= 0 || r.left >= window.innerWidth) {
                el.click();
                return;
            }
            const style = {};
            if (this.dropUp) {
                style.bottom = `${window.innerHeight - r.top}px`;
            } else {
                style.top = `${r.bottom}px`;
            }
            if (this.dropRight) {
                style.right = `${window.innerWidth - r.right}px`;
            } else {
                style.left = `${r.left}px`;
            }
            this.floatingStyle = style;
        },
        onMenuOpen() {
            if (!this.teleport) return;
            this.updatePosition();
            this.bindReposition();
        },
        bindReposition() {
            if (!this.teleport) return;
            window.addEventListener('scroll', this.updatePosition, true); // capture: 중첩 스크롤 컨테이너까지 포착
            window.addEventListener('resize', this.updatePosition);
        },
        unbindReposition() {
            window.removeEventListener('scroll', this.updatePosition, true);
            window.removeEventListener('resize', this.updatePosition);
        },
    },
    beforeUnmount() {
        // 메뉴가 열린 채 언마운트되는 경우 리스너 누수 방지
        this.unbindReposition();
    },
}
</script>
